"use client";

import { useCallback, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";

import { Checkbox } from "@/components/ui/checkbox";
import { Icon } from "@/components/ui/icon";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { SubmitButton } from "@/components/ui/button";
import {
  FloatingWindow,
  FloatingWindowContainer,
  FloatingWindowContent,
  FloatingWindowFooter,
  FloatingWindowHeader,
  FloatingWindowTitle,
} from "@/components/ui/floating-window";
import { showToast } from "@/components/ui/sonner";
import { getApiErrorMessage } from "@/lib/api/error";
import {
  getListSnippetsQueryKey,
  useCreateSnippet,
  useGetSnippet,
  useUpdateSnippet,
} from "@/lib/api/generated";
import { VariableContextProvider } from "@/features/ai-prompts/components/variable";
import { StepEditor } from "@/features/sequences/components/editors/step-editor";

import { SnippetEditorSkeleton } from "./snippet-editor-skeleton";
import {
  snippetFormSchema,
  buildDefaultValues,
  type SnippetFormValues,
} from "../form/schema";

const EDITOR_FEATURES = {
  variables: true,
  snippets: false,
  aiPrompts: false,
  writeWithAi: false,
  formatting: true,
};

export interface InlineSnippetEditorProps {
  mode: "create" | "edit";
  snippetId?: string;
  onClose: () => void;
  /** Fired after a snippet is created — receives its body so the caller can
   *  drop the content into the editor. */
  onCreated?: (bodyDoc: Record<string, unknown>) => void;
  /** Fired after an existing snippet is saved to the library. */
  onSaved?: (snippetId: string) => void;
}

/**
 * Floating snippet editor used from the step-editor snippets panel. Mirrors the
 * inline AI prompt editor so editing never stacks full-screen sheets. Snippets
 * are library-only (not referenced in the sequence), so editing just updates
 * the library; creating optionally inserts the new snippet's content.
 */
export function InlineSnippetEditor({
  mode,
  snippetId,
  onClose,
  onCreated,
  onSaved,
}: InlineSnippetEditorProps) {
  const isEdit = mode === "edit" && !!snippetId;

  const detail = useGetSnippet(snippetId ?? "", { query: { enabled: isEdit } });
  const snippet = detail.data ?? null;
  const showSkeleton = isEdit && detail.isLoading;
  const canEdit = isEdit ? snippet?.can_edit !== false : true;

  const queryClient = useQueryClient();
  const createMutation = useCreateSnippet();
  const updateMutation = useUpdateSnippet();
  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  const form = useForm<SnippetFormValues>({
    resolver: zodResolver(snippetFormSchema),
    mode: "onChange",
    defaultValues: buildDefaultValues(),
  });

  useEffect(() => {
    if (isEdit && snippet) {
      form.reset(
        buildDefaultValues({
          name: snippet.name,
          body_doc: snippet.body_doc as Record<string, unknown>,
          visibility: snippet.visibility as "shared" | "private",
        }),
      );
    } else if (!isEdit) {
      form.reset(buildDefaultValues());
    }
  }, [isEdit, snippet, form]);

  const invalidateLists = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: getListSnippetsQueryKey() });
  }, [queryClient]);

  const onValidSubmit = useCallback(
    async (values: SnippetFormValues) => {
      const payload = {
        name: values.name.trim(),
        body_doc: values.body_doc,
        visibility: values.visibility,
      };

      try {
        if (isEdit && snippetId) {
          await updateMutation.mutateAsync({ snippetId, data: payload });
          showToast.success("Snippet saved");
          invalidateLists();
          onSaved?.(snippetId);
        } else {
          await createMutation.mutateAsync({ data: payload });
          showToast.success("Snippet created");
          invalidateLists();
          onCreated?.(payload.body_doc);
        }
        onClose();
      } catch (err) {
        showToast.error(
          isEdit ? "Couldn't save the snippet" : "Couldn't create the snippet",
          { description: getApiErrorMessage(err, "Please try again.") },
        );
      }
    },
    [
      createMutation,
      invalidateLists,
      isEdit,
      onClose,
      onCreated,
      onSaved,
      snippetId,
      updateMutation,
    ],
  );

  const canSubmit = form.formState.isValid && !isSubmitting && canEdit;
  const isReadOnly = isEdit && !canEdit;
  const showPermissionGuard = isEdit && snippet && !canEdit;

  return (
    <FloatingWindow
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <FloatingWindowContainer className="flex max-h-[80vh] min-h-[60vh] w-[400px] flex-col">
        <FloatingWindowHeader>
          <FloatingWindowTitle>
            <Icon name="IconScissors2" size={16} className="text-heading" />
            <span>{isEdit ? "Edit Snippet" : "New Snippet"}</span>
          </FloatingWindowTitle>
        </FloatingWindowHeader>

        {showSkeleton ? (
          <SnippetEditorSkeleton />
        ) : (
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onValidSubmit)}
              className="flex min-h-0 flex-1 flex-col"
            >
              {showPermissionGuard ? (
                <FloatingWindowContent className="items-center justify-center gap-3">
                  <Icon name="IconLock" size={24} className="text-contrast/30" />
                  <p className="text-center text-sm text-contrast/50">
                    This is a private snippet. You don&apos;t have permission to
                    view or edit its contents.
                  </p>
                </FloatingWindowContent>
              ) : (
                <VariableContextProvider allowBlockEnrollment={false}>
                  <FloatingWindowContent className="gap-5">
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem className="gap-2">
                          <FormLabel className="text-sm font-medium text-foreground">
                            Snippet Name
                          </FormLabel>
                          <FormControl>
                            <Input
                              placeholder="e.g. Personalized Greeting"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="visibility"
                      render={({ field }) => (
                        <FormItem className="gap-2">
                          <FormLabel className="text-sm font-medium text-foreground">
                            Snippet Visibility
                          </FormLabel>
                          <FormControl>
                            <div className="flex flex-col gap-3">
                              <label className="flex cursor-pointer items-center gap-2.5 text-sm font-medium text-foreground">
                                <Checkbox
                                  checked={field.value === "shared"}
                                  onChange={() => field.onChange("shared")}
                                />
                                <span>
                                  Shared (visible to everyone in your
                                  organization)
                                </span>
                              </label>
                              <label className="flex cursor-pointer items-center gap-2.5 text-sm font-medium text-foreground">
                                <Checkbox
                                  checked={field.value === "private"}
                                  onChange={() => field.onChange("private")}
                                />
                                <span>Private (only visible to you)</span>
                              </label>
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <Controller
                      control={form.control}
                      name="body_doc"
                      render={({ field }) => (
                        <div className="flex flex-col gap-2">
                          <span className="text-sm font-medium text-foreground">
                            Snippet Content{" "}
                            <span className="text-destructive">*</span>
                          </span>
                          <StepEditor
                            jobId=""
                            sequenceId=""
                            stepType="automatic_email"
                            value={field.value as Record<string, unknown>}
                            onChange={(doc) => field.onChange(doc)}
                            placeholder="Write your reusable content here…"
                            readOnly={isReadOnly}
                            enabledFeatures={EDITOR_FEATURES}
                            className="max-h-70 min-h-50 overflow-y-auto rounded-lg border border-contrast/15 bg-contrast/6 shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]"
                          />
                        </div>
                      )}
                    />
                  </FloatingWindowContent>
                </VariableContextProvider>
              )}

              {!showPermissionGuard ? (
                <FloatingWindowFooter className="items-center justify-end">
                  <SubmitButton
                    type="submit"
                    size="sm"
                    variant="primary"
                    disabled={!canSubmit}
                    loading={isSubmitting}
                    loadingLabel={isEdit ? "Saving" : "Creating"}
                  >
                    {isEdit ? "Save changes" : "Create snippet"}
                  </SubmitButton>
                </FloatingWindowFooter>
              ) : null}
            </form>
          </Form>
        )}
      </FloatingWindowContainer>
    </FloatingWindow>
  );
}
